/**
 * Transform DRF practice list-view response → GraphQL listPractices shape.
 *
 * The DRF endpoint returns { count, results, practice_networks }.
 * Components expect the Hasura shape with organisationPtr nesting,
 * accreditation arrays, aggregate shapes, and practiceNetworks.
 */

/**
 * Convert Hasura-style filter objects to flat DRF query params.
 * @param {object} hasuraFilters - Hasura _bool_exp style filters
 * @returns {object} - flat query params for DRF endpoint
 */
export function convertFiltersToParams(hasuraFilters) {
  if (!hasuraFilters) return {}

  const params = {}

  // Walk the filter tree and extract known param patterns
  const walk = (obj) => {
    if (!obj || typeof obj !== 'object') return

    // Direct field filters
    if (obj.status?._eq) params.status = obj.status._eq
    if (obj.name?._ilike) params.search = obj.name._ilike.replace(/%/g, '')

    // Physical address filters (nested under organisation_ptr)
    const orgPtr = obj.organisation_ptr || obj.organisationPtr
    if (orgPtr?.physical_address?.state?._eq || orgPtr?.physicalAddress?.state?._eq) {
      params.state = orgPtr.physical_address?.state?._eq || orgPtr.physicalAddress?.state?._eq
    }

    // Network filter
    if (obj.practice_network_name?._eq || obj.practiceNetworkName?._eq) {
      params.network = obj.practice_network_name?._eq || obj.practiceNetworkName?._eq
    }

    // Boolean filters
    if (obj.current_accreditation || obj.currentAccreditation) {
      const ca = obj.current_accreditation || obj.currentAccreditation
      if (ca.status?._eq === 'OPEN') params.cycle_open = 'true'
      if (ca.self_assessment_due || ca.selfAssessmentDue) params.self_assessment_due = 'true'
    }

    // _and arrays
    if (Array.isArray(obj._and)) {
      obj._and.forEach(walk)
    }
    if (Array.isArray(obj._or)) {
      obj._or.forEach(walk)
    }
  }

  walk(hasuraFilters)
  return params
}

/**
 * Transform a single practice item from DRF to match Hasura list shape.
 * @param {object} item - camelCased DRF practice list item
 * @returns {object} - Hasura-compatible practice list item
 */
function transformListItem(item) {
  const accred = item.currentAccreditation

  // Build organisationPtr with physical_address
  const organisationPtr = {
    id: item.id,
    physicalAddress: item.physicalAddress,
  }

  // Build current_accreditation with nested arrays
  let currentAccreditation = null
  if (accred) {
    currentAccreditation = {
      ...accred,
      certificates: accred.currentCertificate ? [accred.currentCertificate] : [],
      visits: accred.finalVisit ? [accred.finalVisit] : [],
      preferredDaysVisits: accred.preferredDaysVisit ? [accred.preferredDaysVisit] : [],
    }
  }

  return {
    ...item,
    organisationPtrId: item.id,
    organisationPtr,
    currentAccreditation,
    surveyVisits: item.surveyVisits || [],
    practiceUsers: item.practiceUsers || [],
    reapplicationsAggregate: {
      aggregate: { count: item.reapplicationsDueCount || 0 },
    },
    reapplicationsPaymentAggregate: {
      aggregate: { count: item.reapplicationsPaymentPendingCount || 0 },
    },
  }
}

/**
 * Transform the full DRF list-view response to match the Hasura listPractices shape.
 * @param {object} drf - camelCased response from GET /api/web.practice/list-view/
 * @returns {object} - shape matching the existing listPractices GraphQL response
 */
export function transformPracticeList(drf) {
  if (!drf) return { practiceList: [], total: { aggregate: { count: 0 } }, practiceNetworks: [] }

  return {
    practiceList: (drf.results || []).map(transformListItem),
    total: {
      aggregate: { count: drf.count || 0 },
    },
    practiceNetworks: (drf.practiceNetworks || []).map(name => ({
      label: name,
      text: name,
      value: name,
    })),
  }
}
